import { Suspense } from "react";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  matchPath,
  useLocation,
  useParams,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";

// Helper component to redirect processing route to review with proper ID
const ProcessingRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/admin/batch-extractions/${id}/review`} replace />;
};

// Back-compat redirect for legacy /scholarships/* paths → /rosters/*.
const ScholarshipsRedirect = () => {
  const { '*': rest } = useParams<{ '*': string }>();
  return <Navigate to={`/rosters/${rest ?? ''}`} replace />;
};
import { LoginRoute } from "./features/login/LoginRoute";
import { SignupPage } from "./features/signup/SignupPage";
import { SignupSessionRoute } from "./features/signup/SignupSessionRoute";
import { RequireTravelBallSignupOpen } from "./features/travel-ball/signup/RequireTravelBallSignupOpen";
import { PricingPage } from "./features/pricing/PricingPage";
import {
  PRIVACY_ALIAS_PATH,
  PRIVACY_PATH,
  TERMS_ALIAS_PATH,
  TERMS_PATH,
} from "./features/legal/legalRoutes";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { PERMISSIONS } from "./utils/permissions";
import { RouteTitle } from "./components/RouteTitle";
import "./App.css";
import { TourProvider } from "./features/tours/TourProvider";
import { AssistantProvider } from "./features/assistant/AssistantProvider";
import { FeatureFlagsProvider } from "./features/feature-flags/FeatureFlagsProvider";
import { DirectorySportProvider } from "./features/directory-sport/DirectorySportProvider";
import { RequireFeature } from "./features/feature-flags/RequireFeature";
import { RequirePlanFeature } from "./features/feature-flags/RequirePlanFeature";
import { PLAN_FEATURES } from "./types/planTiers";
import { AppLoading } from "./components/AppLoading";
import { lazyNamed } from "./utils/lazyNamed";

/*
  Code splitting. Only the routes a signed-out visitor lands on (login, signup, the public price
  list) and the route guards ship in the entry chunk; every other page is fetched the first time it
  renders, so an anonymous /pricing visit does not download the whole authenticated app. The
  `Suspense` around the routes shows the same loader as the auth gate while a chunk arrives.
*/
const ForgotPassword = lazyNamed(() => import("./features/auth/ForgotPassword"), "ForgotPassword");
const AcceptInvite = lazyNamed(() => import("./features/auth/AcceptInvite"), "AcceptInvite");
const SetupProgram = lazyNamed(() => import("./features/auth/SetupProgram"), "SetupProgram");
const SignupPaymentPage = lazyNamed(() => import("./features/signup/SignupPaymentPage"), "SignupPaymentPage");
const SignupCompletePage = lazyNamed(() => import("./features/signup/SignupCompletePage"), "SignupCompletePage");
const LegalPage = lazyNamed(() => import("./features/legal/LegalPage"), "LegalPage");
const TeamDetailPage = lazyNamed(() => import("./features/travel-ball/teams/TeamDetailPage"), "TeamDetailPage");
const TravelBallImportPage = lazyNamed(() => import("./features/travel-ball/teams/import/TravelBallImportPage"), "TravelBallImportPage");
const SignupChooserPage = lazyNamed(() => import("./features/travel-ball/signup/SignupChooserPage"), "SignupChooserPage");
const ProgramSignupPage = lazyNamed(() => import("./features/travel-ball/signup/ProgramSignupPage"), "ProgramSignupPage");
const FamilySignupPage = lazyNamed(() => import("./features/travel-ball/signup/FamilySignupPage"), "FamilySignupPage");
const FamilyHomePage = lazyNamed(() => import("./features/travel-ball/family/FamilyHomePage"), "FamilyHomePage");
const FamilyJoinPage = lazyNamed(() => import("./features/travel-ball/family/FamilyJoinPage"), "FamilyJoinPage");
const FamilyTeamPage = lazyNamed(() => import("./features/travel-ball/family/FamilyTeamPage"), "FamilyTeamPage");
const FamilyAnnouncementsPage = lazyNamed(
  () => import("./features/travel-ball/family/announcements/FamilyAnnouncementsPage"),
  "FamilyAnnouncementsPage",
);
const RedeemInvitePage = lazyNamed(() => import("./features/travel-ball/invite/RedeemInvitePage"), "RedeemInvitePage");
const RosterInviteAcceptPage = lazyNamed(() => import("./features/travel-ball/rosterInvite/RosterInviteAcceptPage"), "RosterInviteAcceptPage");
const AdminHomePage = lazyNamed(() => import("./features/travel-ball/admin/AdminHomePage"), "AdminHomePage");
const AdminTeamsPage = lazyNamed(() => import("./features/travel-ball/admin/AdminTeamsPage"), "AdminTeamsPage");
const AdminPlayersPage = lazyNamed(() => import("./features/travel-ball/admin/AdminPlayersPage"), "AdminPlayersPage");
const AdminCoachesPage = lazyNamed(() => import("./features/travel-ball/admin/AdminCoachesPage"), "AdminCoachesPage");
const AdminSeasonsPage = lazyNamed(() => import("./features/travel-ball/admin/AdminSeasonsPage"), "AdminSeasonsPage");
const AdminSettingsPage = lazyNamed(() => import("./features/travel-ball/admin/AdminSettingsPage"), "AdminSettingsPage");
const AccountSettingsPage = lazyNamed(() => import("./features/travel-ball/account/AccountSettingsPage"), "AccountSettingsPage");
const CoachAnnouncementsPage = lazyNamed(
  () => import("./features/travel-ball/announcements/CoachAnnouncementsPage"),
  "CoachAnnouncementsPage",
);
const CoachMessagesPage = lazyNamed(
  () => import("./features/travel-ball/messages/CoachMessagesPage"),
  "CoachMessagesPage",
);
const FamilyMessagesPage = lazyNamed(
  () => import("./features/travel-ball/family/messages/FamilyMessagesPage"),
  "FamilyMessagesPage",
);
const CoachHomePage = lazyNamed(() => import("./features/travel-ball/coach/CoachHomePage"), "CoachHomePage");
const CoachMyPlayersPage = lazyNamed(() => import("./features/travel-ball/coach/CoachMyPlayersPage"), "CoachMyPlayersPage");
const AuthLandingRoute = lazyNamed(() => import("./features/landing/AuthLandingRoute"), "AuthLandingRoute");
const AuthActionPage = lazyNamed(() => import("./features/auth/AuthActionPage"), "AuthActionPage");
const DashboardScreen = lazyNamed(() => import("./features/dashboard/DashboardScreen"), "DashboardScreen");
const PlayersSection = lazyNamed(() => import("./features/dashboard/sections/players/PlayersSection"), "PlayersSection");
const DuplicatesSection = lazyNamed(() => import("./features/dashboard/sections/duplicates/DuplicatesSection"), "DuplicatesSection");
const TrustedContactsSection = lazyNamed(() => import("./features/trusted-contacts/TrustedContactsSection"), "TrustedContactsSection");
const CallsListSection = lazyNamed(() => import("./features/dashboard/sections/calls/CallsListSection"), "CallsListSection");
const ScheduleSection = lazyNamed(() => import("./features/dashboard/sections/schedule/ScheduleSection"), "ScheduleSection");
const TournamentFinderPage = lazyNamed(() => import("./features/tournament-finder/TournamentFinderPage"), "TournamentFinderPage");
const TournamentDetailPage = lazyNamed(() => import("./features/tournament-finder/TournamentDetailPage"), "TournamentDetailPage");
const PitchChartPage = lazyNamed(() => import("./features/pitch-charts/PitchChartPage"), "PitchChartPage");
const SearchTravelBallPage = lazyNamed(() => import("./features/search-travel-ball/SearchTravelBallPage"), "SearchTravelBallPage");
const SearchTravelBallProgramDetailPage = lazyNamed(() => import("./features/search-travel-ball/ProgramDetailPage"), "ProgramDetailPage");
const SearchTravelBallTeamRosterPage = lazyNamed(() => import("./features/search-travel-ball/TeamRosterPage"), "TeamRosterPage");
const SearchTravelBallPlayerDetailPage = lazyNamed(() => import("./features/search-travel-ball/PlayerDetailPage"), "PlayerDetailPage");
const AdminTravelBallOrganizationsPage = lazyNamed(() => import("./features/admin/travel-ball/TravelBallOrganizationsPage"), "TravelBallOrganizationsPage");
const SupportPage = lazyNamed(() => import("./features/support/SupportPage"), "SupportPage");
const SupportTicketPage = lazyNamed(() => import("./features/support/SupportTicketPage"), "SupportTicketPage");
const HomePage = lazyNamed(() => import("./features/home/HomePage"), "HomePage");
const PlayerDatabasePage = lazyNamed(() => import("./features/player-database/PlayerDatabasePage"), "PlayerDatabasePage");
const SchedulePrintPage = lazyNamed(() => import("./features/dashboard/sections/schedule/print/SchedulePrintPage"), "SchedulePrintPage");
const BuildItineraryWizardRoute = lazyNamed(() => import("./features/dashboard/sections/schedule/build-itinerary/BuildItineraryWizard"), "BuildItineraryWizardRoute");
const VideosSection = lazyNamed(() => import("./features/dashboard/sections/videos/VideosSection"), "VideosSection");
const LatestNotesSection = lazyNamed(() => import("./features/dashboard/sections/notes/LatestNotesSection"), "LatestNotesSection");
const CreateRecruitingNotePage = lazyNamed(() => import("./features/dashboard/sections/notes/CreateRecruitingNotePage"), "CreateRecruitingNotePage");
const ScholarshipsSection = lazyNamed(() => import("./features/dashboard/sections/scholarships/ScholarshipsSection"), "ScholarshipsSection");
const RosterImageSection = lazyNamed(() => import("./features/dashboard/sections/rosters/RosterImageSection"), "RosterImageSection");
const ScholarshipDetailLayout = lazyNamed(() => import("./features/dashboard/sections/scholarships/ScholarshipDetailLayout"), "ScholarshipDetailLayout");
const ScholarshipPlayersTab = lazyNamed(() => import("./features/dashboard/sections/scholarships/ScholarshipPlayersTab"), "ScholarshipPlayersTab");
const ScholarshipDepthChartTab = lazyNamed(() => import("./features/dashboard/sections/scholarships/ScholarshipDepthChartTab"), "ScholarshipDepthChartTab");
const ScholarshipPitchingStaffTab = lazyNamed(() => import("./features/dashboard/sections/scholarships/ScholarshipPitchingStaffTab"), "ScholarshipPitchingStaffTab");
const ScholarshipLineupsTab = lazyNamed(() => import("./features/dashboard/sections/scholarships/ScholarshipLineupsTab"), "ScholarshipLineupsTab");
const AdminLayout = lazyNamed(() => import("./features/admin/AdminLayout"), "AdminLayout");
const AdminSection = lazyNamed(() => import("./features/admin/AdminSection"), "AdminSection");
const OrganizationDetail = lazyNamed(() => import("./features/admin/OrganizationDetail"), "OrganizationDetail");
const NotificationsSection = lazyNamed(() => import("./features/admin/NotificationsSection"), "NotificationsSection");
const MessagingNumbersSection = lazyNamed(() => import("./features/admin/MessagingNumbersSection"), "MessagingNumbersSection");
const AdminItineraryRequestsSection = lazyNamed(() => import("./features/admin/AdminItineraryRequestsSection"), "AdminItineraryRequestsSection");
const SampleDataSection = lazyNamed(() => import("./features/admin/sample-data/SampleDataSection"), "SampleDataSection");
const DirectorySection = lazyNamed(() => import("./features/admin/directory/DirectorySection"), "DirectorySection");
const DirectorySourcesTab = lazyNamed(() => import("./features/admin/directory/DirectorySourcesTab"), "DirectorySourcesTab");
const FeedHealthTab = lazyNamed(() => import("./features/admin/directory/health/FeedHealthTab"), "FeedHealthTab");
const DirectoryRunsTab = lazyNamed(() => import("./features/admin/directory/DirectoryRunsTab"), "DirectoryRunsTab");
const DirectoryTournamentsTab = lazyNamed(() => import("./features/admin/directory/DirectoryTournamentsTab"), "DirectoryTournamentsTab");
const DirectoryReviewTab = lazyNamed(() => import("./features/admin/directory/DirectoryReviewTab"), "DirectoryReviewTab");
const DirectoryPlayerReviewTab = lazyNamed(() => import("./features/admin/directory/players/DirectoryPlayerReviewTab"), "DirectoryPlayerReviewTab");
const DirectoryPlayersTab = lazyNamed(() => import("./features/admin/directory/players/DirectoryPlayersTab"), "DirectoryPlayersTab");
const DirectoryRankingsTab = lazyNamed(() => import("./features/admin/directory/rankings/DirectoryRankingsTab"), "DirectoryRankingsTab");
const DirectorySuppressionsTab = lazyNamed(() => import("./features/admin/directory/players/DirectorySuppressionsTab"), "DirectorySuppressionsTab");
const DirectoryProgramsTab = lazyNamed(() => import("./features/admin/directory/college/ProgramsTab"), "ProgramsTab");
const DirectorySosTab = lazyNamed(() => import("./features/admin/directory/college/SosTab"), "SosTab");
const DirectoryCollegeRankingsTab = lazyNamed(
  () => import("./features/admin/directory/college/CollegeRankingsTab"),
  "CollegeRankingsTab"
);
const DirectoryStatLinesTab = lazyNamed(() => import("./features/admin/directory/college/UnmatchedStatLinesTab"), "UnmatchedStatLinesTab");
const DirectoryRankingsExportTab = lazyNamed(() => import("./features/admin/directory/exports/RankingsExportTab"), "RankingsExportTab");
const FeatureFlagsSection = lazyNamed(() => import("./features/admin/feature-flags/FeatureFlagsSection"), "FeatureFlagsSection");
const ToursSection = lazyNamed(() => import("./features/admin/tours/ToursSection"), "ToursSection");
const TourEditor = lazyNamed(() => import("./features/admin/tours/TourEditor"), "TourEditor");
const BatchExtractionsSection = lazyNamed(() => import("./features/admin/batch-extractions/BatchExtractionsSection"), "BatchExtractionsSection");
const RosterUploadsSection = lazyNamed(() => import("./features/admin/roster-uploads/RosterUploadsSection"), "RosterUploadsSection");
const RosterUploadDetail = lazyNamed(() => import("./features/admin/roster-uploads/RosterUploadDetail"), "RosterUploadDetail");
const BatchExtractionWizard = lazyNamed(() => import("./features/admin/batch-extractions/BatchExtractionWizard"), "BatchExtractionWizard");
const BatchExtractionReview = lazyNamed(() => import("./features/admin/batch-extractions/BatchExtractionReview"), "BatchExtractionReview");
const ScrapingSection = lazyNamed(() => import("./features/admin/scraping/ScrapingSection"), "ScrapingSection");
const BillingSection = lazyNamed(() => import("./features/admin/billing/BillingSection"), "BillingSection");
const BillingOrganizationDetail = lazyNamed(() => import("./features/admin/billing/BillingOrganizationDetail"), "BillingOrganizationDetail");
const BillingSettingsSection = lazyNamed(() => import("./features/admin/billing/BillingSettingsSection"), "BillingSettingsSection");
const SelfServeSection = lazyNamed(() => import("./features/admin/self-serve/SelfServeSection"), "SelfServeSection");
const SelfServeSubscriptionsTab = lazyNamed(() => import("./features/admin/self-serve/SelfServeSubscriptionsTab"), "SelfServeSubscriptionsTab");
const SelfServeTiersTab = lazyNamed(() => import("./features/admin/self-serve/SelfServeTiersTab"), "SelfServeTiersTab");
const SelfServeEmailRulesTab = lazyNamed(() => import("./features/admin/self-serve/SelfServeEmailRulesTab"), "SelfServeEmailRulesTab");
const PlanTiersTab = lazyNamed(() => import("./features/admin/self-serve/PlanTiersTab"), "PlanTiersTab");

/** The Player Database page needs either of these; each of its lists needs one (`playerDatabaseView.ts`). */
const PLAYER_DATABASE_PLAN_FEATURES = [PLAN_FEATURES.playerDatabase, PLAN_FEATURES.transferPortal] as const;
const OrgSettingsLayout = lazyNamed(() => import("./features/settings/OrgSettingsLayout"), "OrgSettingsLayout");
const UsersSection = lazyNamed(() => import("./features/settings/UsersSection"), "UsersSection");
const MyBillingPage = lazyNamed(() => import("./features/billing/MyBillingPage"), "MyBillingPage");
const BrandingSettingsPage = lazyNamed(
  () => import("./features/settings/branding/BrandingSettingsPage"),
  "BrandingSettingsPage",
);
const UserDetailPage = lazyNamed(() => import("./features/settings/UserDetailPage"), "UserDetailPage");
const PlayerSubmissionForm = lazyNamed(() => import("./features/public/PlayerSubmissionForm"), "PlayerSubmissionForm");
const CrmLayout = lazyNamed(() => import("./features/crm/CrmLayout"), "CrmLayout");
const CommunicationsPage = lazyNamed(() => import("./features/crm/communications/CommunicationsPage"), "CommunicationsPage");
const ConnectorSettingsPage = lazyNamed(() => import("./features/crm/connectors/ConnectorSettingsPage"), "ConnectorSettingsPage");
const XPostTemplateAdminPage = lazyNamed(() => import("./features/crm/settings/XPostTemplateAdminPage"), "XPostTemplateAdminPage");
const MessagingLayout = lazyNamed(() => import("./features/messaging/MessagingLayout"), "MessagingLayout");
const MessagingEnabledRoute = lazyNamed(() => import("./features/messaging/MessagingEnabledRoute"), "MessagingEnabledRoute");
const MessagingInboxPage = lazyNamed(() => import("./features/messaging/MessagingInboxPage"), "MessagingInboxPage");
const MessageTemplatesPage = lazyNamed(() => import("./features/messaging/MessageTemplatesPage"), "MessageTemplatesPage");
const PhoneNumbersPage = lazyNamed(() => import("./features/messaging/PhoneNumbersPage"), "PhoneNumbersPage");
const UnmatchedQueuePage = lazyNamed(() => import("./features/crm/messages/UnmatchedQueuePage"), "UnmatchedQueuePage");
const FollowersTabPage = lazyNamed(() => import("./features/crm/twitter/FollowersTabPage"), "FollowersTabPage");
const PendingSubmissionsPage = lazyNamed(() => import("./features/crm/submissions/PendingSubmissionsPage"), "PendingSubmissionsPage");
const CrmImportPage = lazyNamed(() => import("./features/crm/import/CrmImportPage"), "CrmImportPage");
const ContactListPage = lazyNamed(() => import("./features/crm/contacts/ContactListPage"), "ContactListPage");
const ContactDetailView = lazyNamed(() => import("./features/crm/contacts/ContactDetailView"), "ContactDetailView");
const TeamsListPage = lazyNamed(() => import("./features/crm/teams/TeamsListPage"), "TeamsListPage");
const CrmTeamDetailView = lazyNamed(() => import("./features/crm/teams/CrmTeamDetailView"), "CrmTeamDetailView");

/**
 * Public routes that render without knowing who is signed in, so they skip the wait for Firebase to
 * restore the session. /pricing: the marketing site links anonymous visitors to it, and waiting on
 * auth would hold back its price fetch for nothing. The legal pages and their aliases: static text
 * anyone may be sent to, which should never sit behind a loader. Each renders the same element
 * signed in or out, so nothing remounts when auth settles.
 */
const ROUTES_WITHOUT_AUTH_WAIT = [
  "/pricing",
  TERMS_PATH,
  PRIVACY_PATH,
  TERMS_ALIAS_PATH,
  PRIVACY_ALIAS_PATH,
];

export const AppRoutes = () => {
  const { initializing, effectiveOrganizationId } = useAuth();
  // Org-scoped pages that hold calendar state are keyed on the org, so an impersonation switch
  // remounts them instead of showing one org's "On your calendar" to another.
  const orgKey = effectiveOrganizationId ?? "none";
  const { pathname } = useLocation();
  const skipsAuthWait = ROUTES_WITHOUT_AUTH_WAIT.some((path) => matchPath(path, pathname));

  if (initializing && !skipsAuthWait) {
    return <AppLoading />;
  }

  return (
    // A chunk that still fails after lazyNamed's one reload lands here rather than blanking the app.
    <ErrorBoundary
      fallbackTitle="This page did not load"
      fallbackMessage="RosterPilot may have just been updated. Refresh the page to load the latest version."
    >
    <Suspense fallback={<AppLoading />}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        {/*
          Firebase's custom email action URL is one of these two, shared by every template (password
          reset and email verification alike), so both switch on `mode`. /reset-password keeps its
          reset flow for any other mode. Public, and must stay outside ProtectedRoute - the coach
          verifying an address has no RosterPilot user yet.
        */}
        <Route path="/reset-password" element={<AuthActionPage resetByDefault />} />
        <Route path="/auth/complete-reset" element={<AuthActionPage />} />
        {/*
          Public on purpose: the person accepting an invitation has no account yet, which is the whole
          point. It must never sit inside ProtectedRoute.
        */}
        <Route path="/accept-invite" element={<AcceptInvite />} />
        {/*
          A platform admin's setup invitation: the invitee creates a new program and becomes its admin.
          Public for the same reason as /accept-invite, and must never sit inside ProtectedRoute.
        */}
        <Route path="/setup-program" element={<SetupProgram />} />
        {/*
          Self-serve signup. /signup is public for the same reason as /accept-invite. The two after it
          need a session but must NOT sit inside ProtectedRoute, which is what sends an organization
          awaiting payment to them: /signup/payment is also Stripe's cancel URL and /signup/complete
          its success URL.
        */}
        <Route path="/signup" element={<SignupPage />} />
        <Route
          path="/signup/payment"
          element={
            <SignupSessionRoute>
              <SignupPaymentPage />
            </SignupSessionRoute>
          }
        />
        <Route
          path="/signup/complete"
          element={
            <SignupSessionRoute>
              <SignupCompletePage />
            </SignupSessionRoute>
          }
        />
        {/*
          The public price list the marketing site links to. Public like /signup, and must never sit
          inside ProtectedRoute: a coach reading prices has no account yet. It works signed in too.
        */}
        <Route path="/pricing" element={<PricingPage />} />
        {/*
          Terms of Service and Privacy Policy, rendered from the marketing site's own pages. Public
          like /pricing, and must never sit inside ProtectedRoute: they are linked from outside the
          app. The two longer names redirect so either spelling of the link works.
        */}
        <Route path={TERMS_PATH} element={<LegalPage kind="terms" />} />
        <Route path={PRIVACY_PATH} element={<LegalPage kind="privacy" />} />
        <Route path={TERMS_ALIAS_PATH} element={<Navigate to={TERMS_PATH} replace />} />
        <Route path={PRIVACY_ALIAS_PATH} element={<Navigate to={PRIVACY_PATH} replace />} />
        <Route path="/submit/:orgToken" element={<PlayerSubmissionForm />} />
        {/*
          Travel ball signup. Public like /signup (no RosterPilot college profile exists for a
          TB-only account), and must never sit inside ProtectedRoute, which requires a college users
          row. RequireTravelBallSignupOpen shows each page only once the public availability read says
          travelball.foundation is on (fails closed), because the pages create the Firebase account
          before any API call; the server still refuses the create endpoints with the flag off. The old one-team coach
          signup is gone (every team belongs to a program); its path still arrives from older
          verification emails, so it lands on the chooser.
        */}
        <Route
          path="/travel-ball/signup"
          element={
            <RequireTravelBallSignupOpen>
              <SignupChooserPage />
            </RequireTravelBallSignupOpen>
          }
        />
        <Route path="/travel-ball/signup/solo" element={<Navigate to="/travel-ball/signup" replace />} />
        <Route
          path="/travel-ball/signup/program"
          element={
            <RequireTravelBallSignupOpen>
              <ProgramSignupPage />
            </RequireTravelBallSignupOpen>
          }
        />
        <Route
          path="/travel-ball/signup/family"
          element={
            <RequireTravelBallSignupOpen>
              <FamilySignupPage />
            </RequireTravelBallSignupOpen>
          }
        />
        {/*
          Invite redemption is public: an anonymous user may land here from a share link and sign up
          as part of redeeming. Signed-in users (a travel ball account joining its first program, or
          an already-member hitting the page) are handled inside the component.
        */}
        <Route path="/travel-ball/accept-invite" element={<RedeemInvitePage />} />
        {/*
          A coach's roster invite, for a player or parent/guardian. Public for the same reason: the
          invitee may have no account yet, and a signed-in one may be the wrong account. The token is
          in the fragment (#token=), read once and cleared by the page.
        */}
        <Route path="/travel-ball/roster-invite" element={<RosterInviteAcceptPage />} />
        {/*
          The old one-team coach workspace is gone (every team belongs to a program). Its bookmarks
          go to the landing router, which picks the right home.
        */}
        <Route path="/travel-ball/solo/*" element={<Navigate to="/" replace />} />
        <Route
          path="/travel-ball/coach"
          element={
            <ProtectedRoute kind="travelball">
              <CoachHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/coach/players"
          element={
            <ProtectedRoute kind="travelball">
              <CoachMyPlayersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/teams/:teamId"
          element={
            <ProtectedRoute kind="travelball">
              <TeamDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/teams/:teamId/import"
          element={
            <ProtectedRoute kind="travelball">
              <TravelBallImportPage />
            </ProtectedRoute>
          }
        />
        {/*
          Program admin surface. Every route sits inside ProtectedRoute with kind="travelball"; the
          server enforces the admin role via [RequireTravelBallRole("admin")] on each endpoint, so a
          coach reaching the URL by hand gets 403s on every data call and sees error states rather
          than someone else's data.
        */}
        <Route
          path="/travel-ball/admin"
          element={
            <ProtectedRoute kind="travelball">
              <AdminHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/teams"
          element={
            <ProtectedRoute kind="travelball">
              <AdminTeamsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/players"
          element={
            <ProtectedRoute kind="travelball">
              <AdminPlayersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/coaches"
          element={
            <ProtectedRoute kind="travelball">
              <AdminCoachesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/seasons"
          element={
            <ProtectedRoute kind="travelball">
              <AdminSeasonsPage />
            </ProtectedRoute>
          }
        />
        {/*
          The coach's Announcements tab, one per coach workspace. The rail shows it only when the
          caller coaches a team they can post on; reached by hand otherwise, the page says so.
        */}
        <Route
          path="/travel-ball/coach/announcements"
          element={
            <ProtectedRoute kind="travelball">
              <CoachAnnouncementsPage shell="coach" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/announcements"
          element={
            <ProtectedRoute kind="travelball">
              <CoachAnnouncementsPage shell="admin" />
            </ProtectedRoute>
          }
        />
        {/*
          The coach's and admin's Messages, one per coach workspace, with the open conversation (or
          `new`) in the path so a thread can be linked. The rail shows it only while the feature is on.
        */}
        <Route
          path="/travel-ball/coach/messages/:conversationId?"
          element={
            <ProtectedRoute kind="travelball">
              <CoachMessagesPage shell="coach" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/messages/:conversationId?"
          element={
            <ProtectedRoute kind="travelball">
              <CoachMessagesPage shell="admin" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/coach/account"
          element={
            <ProtectedRoute kind="travelball">
              <AccountSettingsPage shell="coach" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/account"
          element={
            <ProtectedRoute kind="travelball">
              <AccountSettingsPage shell="admin" />
            </ProtectedRoute>
          }
        />
        {/*
          Player and parent/guardian pages. kind="travelball_family" keeps coach views off them, and
          kind="travelball" on every coach route keeps family views off those: the API refuses a
          family view on every coach `/api/travel-ball/*` route.
        */}
        <Route
          path="/travel-ball/family"
          element={
            <ProtectedRoute kind="travelball_family">
              <FamilyHomePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/family/announcements"
          element={
            <ProtectedRoute kind="travelball_family">
              <FamilyAnnouncementsPage />
            </ProtectedRoute>
          }
        />
        {/*
          A player's or parent's Messages; a guardian's read-only view of one linked player's
          conversations is under children/:playerUserId.
        */}
        <Route
          path="/travel-ball/family/messages/:conversationId?"
          element={
            <ProtectedRoute kind="travelball_family">
              <FamilyMessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/family/messages/children/:playerUserId/:conversationId?"
          element={
            <ProtectedRoute kind="travelball_family">
              <FamilyMessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/family/join"
          element={
            <ProtectedRoute kind="travelball_family">
              <FamilyJoinPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/family/teams/:teamId"
          element={
            <ProtectedRoute kind="travelball_family">
              <FamilyTeamPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/family/account"
          element={
            <ProtectedRoute kind="travelball_family">
              <AccountSettingsPage shell="family" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/travel-ball/admin/settings"
          element={
            <ProtectedRoute kind="travelball">
              <AdminSettingsPage />
            </ProtectedRoute>
          }
        />

        {/*
          The printable schedule sits outside the dashboard layout on purpose: the page IS the
          printout, so no nav, header or banners. Same Page Access key as /schedule.
        */}
        <Route
          path="/schedule/print"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.schedule}>
              <SchedulePrintPage />
            </ProtectedRoute>
          }
        />

        {/*
          Main application routes - shared layout. College only: a travel ball view has no college
          user, so every request here would fail. A travel ball view that lands here is sent to "/",
          which routes it to its own home.
        */}
        <Route
          element={
            <ProtectedRoute kind="college">
              <DashboardScreen />
            </ProtectedRoute>
          }
        >
          {/*
            The college coach's Home page. Flag outside permission, like the Tournament Finder below;
            the flag is on by default and is a kill switch.
          */}
          <Route
            path="/home"
            element={
              <RequireFeature flag="home.dashboard">
                <ProtectedRoute requiredPermission={PERMISSIONS.home} explainDenial>
                  <HomePage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          <Route path="/players" element={<PlayersSection />} />
          {/*
            A pitch chart, opened from a player's Pitch Data section. No Page Access key, like the
            At-Bat Log it sits beside on the always-available Players page; the API scopes it to the org.
          */}
          <Route path="/pitch-charts/:id" element={<PitchChartPage key={orgKey} />} />
          <Route
            path="/trusted-contacts"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.trustedContacts} explainDenial>
                <TrustedContactsSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/duplicates"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.duplicates}>
                <DuplicatesSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/calls"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.calls}>
                <CallsListSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.schedule}>
                <ScheduleSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/schedule/build-itinerary"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.schedule}>
                <BuildItineraryWizardRoute />
              </ProtectedRoute>
            }
          />
          {/*
            Tournament Finder. The flag guard sits outside the permission guard, so while the flag is
            off the page reads as absent (the API's 404), not as "you need access".
          */}
          <Route
            path="/tournaments"
            element={
              <RequireFeature flag="directory.tournaments">
                <ProtectedRoute requiredPermission={PERMISSIONS.tournaments} explainDenial>
                  <TournamentFinderPage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          <Route
            path="/tournaments/:id"
            element={
              <RequireFeature flag="directory.tournaments">
                <ProtectedRoute requiredPermission={PERMISSIONS.tournaments} explainDenial>
                  <TournamentDetailPage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          {/* Player Database. Flag outside permission, like the Tournament Finder above. */}
          <Route
            path="/player-database"
            element={
              <RequireFeature flag="directory.players">
                <RequirePlanFeature anyOf={PLAYER_DATABASE_PLAN_FEATURES}>
                  <ProtectedRoute requiredPermission={PERMISSIONS.playerSearch} explainDenial>
                    <PlayerDatabasePage key={orgKey} />
                  </ProtectedRoute>
                </RequirePlanFeature>
              </RequireFeature>
            }
          />
          {/*
            Phase 4: Travel Ball discovery for college coaches. Flag outside the protected route so
            the pages read as absent when travelball.foundation is off, then the travel_ball_search
            Page Access key like the Player Database. The server also enforces both, and TB-only
            users (403), but the pages won't be reachable from the nav for them anyway.
          */}
          <Route
            path="/search-travel-ball"
            element={
              <RequireFeature flag="travelball.foundation">
                <ProtectedRoute requiredPermission={PERMISSIONS.travelBallSearch} explainDenial>
                  <SearchTravelBallPage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          <Route
            path="/search-travel-ball/programs/:programId"
            element={
              <RequireFeature flag="travelball.foundation">
                <ProtectedRoute requiredPermission={PERMISSIONS.travelBallSearch} explainDenial>
                  <SearchTravelBallProgramDetailPage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          <Route
            path="/search-travel-ball/programs/:programId/teams/:teamId"
            element={
              <RequireFeature flag="travelball.foundation">
                <ProtectedRoute requiredPermission={PERMISSIONS.travelBallSearch} explainDenial>
                  <SearchTravelBallTeamRosterPage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          <Route
            path="/search-travel-ball/players/:playerId"
            element={
              <RequireFeature flag="travelball.foundation">
                <ProtectedRoute requiredPermission={PERMISSIONS.travelBallSearch} explainDenial>
                  <SearchTravelBallPlayerDetailPage key={orgKey} />
                </ProtectedRoute>
              </RequireFeature>
            }
          />
          {/*
            Support: the user's own help (contact support, guided tours, Get Started), so no Page
            Access key and no flag on the page itself; only its Contact support tab and the thread
            route below are dark behind `support.helpdesk`. Keyed by organization like the pages
            above, so a system admin switching org does not keep the previous org's thread on screen.
          */}
          <Route path="/support" element={<SupportPage key={orgKey} />} />
          <Route
            path="/support/:ticketId"
            element={
              <RequireFeature flag="support.helpdesk">
                <SupportTicketPage key={orgKey} />
              </RequireFeature>
            }
          />
          <Route
            path="/video"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.video}>
                <VideosSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/latest-notes"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.notes}>
                <LatestNotesSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiting-notes/new"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.notes}>
                <CreateRecruitingNotePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rosters"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.rosters}>
                <ScholarshipsSection />
              </ProtectedRoute>
            }
          />
          {/* Static segment must precede the `:id` route so it isn't captured as a scholarship id. */}
          <Route
            path="/rosters/roster-images"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.rosters}>
                <RosterImageSection />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rosters/:id"
            element={
              <ProtectedRoute requiredPermission={PERMISSIONS.rosters}>
                <ScholarshipDetailLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="players" replace />} />
            <Route path="players" element={<ScholarshipPlayersTab />} />
            <Route path="depth-chart" element={<ScholarshipDepthChartTab />} />
            <Route path="pitching-staff" element={<ScholarshipPitchingStaffTab />} />
            <Route path="lineups" element={<ScholarshipLineupsTab />} />
            <Route path="*" element={<Navigate to="players" replace />} />
          </Route>
          {/* Back-compat: redirect old /scholarships paths to /rosters. */}
          <Route path="/scholarships" element={<Navigate to="/rosters" replace />} />
          <Route path="/scholarships/*" element={<ScholarshipsRedirect />} />
        </Route>

        {/* System Admin routes - nested under AdminLayout */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminSection />} />
          <Route path="notifications" element={<NotificationsSection />} />
          <Route path="organizations/:id" element={<OrganizationDetail />} />
          <Route path="roster-uploads" element={<RosterUploadsSection />} />
          <Route path="roster-uploads/:id" element={<RosterUploadDetail />} />
          <Route path="batch-extractions" element={<BatchExtractionsSection />} />
          <Route path="batch-extractions/new" element={<BatchExtractionWizard />} />
          {/* Redirect processing to review for backward compatibility */}
          <Route path="batch-extractions/:id/processing" element={<ProcessingRedirect />} />
          <Route path="batch-extractions/:id/review" element={<BatchExtractionReview />} />
          <Route path="scraping" element={<ScrapingSection />} />
          <Route path="messaging-numbers" element={<MessagingNumbersSection />} />
          <Route path="itinerary-requests" element={<AdminItineraryRequestsSection />} />
          <Route path="sample-data" element={<SampleDataSection />} />
          <Route path="billing" element={<BillingSection />} />
          {/* Settings before the :orgId detail so "settings" is never read as an organization id. */}
          <Route path="billing/settings" element={<BillingSettingsSection />} />
          <Route path="billing/organizations/:orgId" element={<BillingOrganizationDetail />} />
          <Route path="self-serve" element={<SelfServeSection />}>
            <Route index element={<SelfServeSubscriptionsTab />} />
            <Route path="tiers" element={<SelfServeTiersTab />} />
            <Route path="email-rules" element={<SelfServeEmailRulesTab />} />
            <Route path="plans" element={<PlanTiersTab />} />
          </Route>
          <Route path="tours" element={<ToursSection />} />
          <Route path="feature-flags" element={<FeatureFlagsSection />} />
          <Route path="directory" element={<DirectorySection />}>
            <Route index element={<DirectorySourcesTab />} />
            <Route path="health" element={<FeedHealthTab />} />
            <Route path="runs" element={<DirectoryRunsTab />} />
            <Route path="tournaments" element={<DirectoryTournamentsTab />} />
            <Route path="review" element={<DirectoryReviewTab />} />
            <Route path="player-review" element={<DirectoryPlayerReviewTab />} />
            <Route path="players" element={<DirectoryPlayersTab />} />
            <Route path="suppressions" element={<DirectorySuppressionsTab />} />
            <Route path="exports" element={<DirectoryRankingsExportTab />} />
            <Route path="rankings" element={<DirectoryRankingsTab />} />
            <Route path="programs" element={<DirectoryProgramsTab />} />
            <Route path="sos" element={<DirectorySosTab />} />
            <Route path="stat-lines" element={<DirectoryStatLinesTab />} />
            <Route path="college-rankings" element={<DirectoryCollegeRankingsTab />} />
          </Route>
          <Route path="tours/:tourKey" element={<TourEditor />} />
          {/* Phase 4: sys-admin TB programs management (discoverable toggle, price, pause). */}
          <Route path="travel-ball/organizations" element={<AdminTravelBallOrganizationsPage />} />
        </Route>

        {/* Organization Settings route - for org admins and system admins */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute allowedRoles={["admin", "system_admin"]}>
              <OrgSettingsLayout />
            </ProtectedRoute>
          }
        >
          {/*
            No settings landing page: it was a card that only linked to Users, and the left nav now
            expands to the destinations directly. /settings stays valid so old links still work.
          */}
          <Route index element={<Navigate to="/settings/users" replace />} />
          <Route path="users" element={<UsersSection />} />
          <Route path="users/:id" element={<UserDetailPage />} />
          {/*
            The organization's own bill. Under /settings because it is already role-gated to admin
            and system_admin there, which is exactly who should see it - a new top-level destination
            would need its own Page Access key to withhold from a coach, and this needs no such
            thing since a coach cannot reach /settings at all.
          */}
          <Route path="billing" element={<MyBillingPage />} />
          <Route path="branding" element={<BrandingSettingsPage />} />
        </Route>

        {/* CRM Communication routes - system admins only; the API matches */}
        <Route
          path="/crm"
          element={
            <ProtectedRoute allowedRoles={["system_admin"]}>
              <CrmLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/crm/contacts" replace />} />
          <Route path="contacts" element={<ContactListPage />} />
          <Route path="contacts/:id" element={<ContactDetailView />} />
          <Route path="import" element={<CrmImportPage />} />
          <Route path="communications" element={<CommunicationsPage />} />
          <Route path="messages/unmatched" element={<UnmatchedQueuePage />} />
          <Route path="twitter/followers" element={<FollowersTabPage />} />
          <Route path="submissions/pending" element={<PendingSubmissionsPage />} />
          <Route path="teams" element={<TeamsListPage />} />
          <Route path="teams/:id" element={<CrmTeamDetailView />} />
          <Route path="connectors" element={<ConnectorSettingsPage />} />
          <Route path="settings/x-post-templates" element={<XPostTemplateAdminPage />} />
        </Route>

        {/* Messaging — a destination of its own, not a CRM section. Open to every coach, because a
            coach texting a recruit is the feature; only number administration is admin-gated. */}
        <Route
          path="/messaging"
          element={
            <ProtectedRoute requiredPermission={PERMISSIONS.messaging}>
              <MessagingEnabledRoute>
                <MessagingLayout />
              </MessagingEnabledRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<MessagingInboxPage />} />
          <Route path="templates" element={<MessageTemplatesPage />} />
          <Route
            path="numbers"
            element={
              <ProtectedRoute allowedRoles={["admin", "system_admin"]}>
                <PhoneNumbersPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/messaging" replace />} />
        </Route>

        {/*
          Phase 1b.5: `/` resolves against the user's active workspace ("view"). College → /players,
          TB program → its admin or coach home, family → /travel-ball/family, no views → /login with
          the "not linked" banner, signed out → /login. Replaces the old pre-poly-view `user ? "/players" : "/login"` branch.
        */}
        <Route path="/" element={<AuthLandingRoute />} />

        {/* Catch-all for unknown paths — same decision as `/` so an orphan or a TB-only user does
            not get dumped on /players when they land on a mistyped URL. */}
        <Route path="*" element={<AuthLandingRoute />} />
      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
};

export const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/*
         * A sibling of the routes, not a wrapper: sibling effects run in render
         * order, so a page that titles itself from loaded data still wins over
         * the static map here.
         */}
        <RouteTitle />
        <FeatureFlagsProvider>
          <DirectorySportProvider>
            <TourProvider>
              {/* The app-wide assistant's conversation, kept across pages and shells (ai.assistant). */}
              <AssistantProvider>
                <AppRoutes />
              </AssistantProvider>
            </TourProvider>
          </DirectorySportProvider>
        </FeatureFlagsProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};
